import { Container, Text, Ticker } from "pixi.js";
import { container } from "tsyringe";
import ControlEvent from "../../../core/control/event/ControlEvent";
import { Tweener } from "../../../core/tweener/engineTween";
import LayoutBuilder from "../../../core/utils/LayoutBuilder";
import LayoutElement from "../../../core/view/model/LayoutElement";
import { UIEvent } from "../../../slots/control/event/UIEvent";
import { SlotMachineEvent } from "../../../slots/model/event/SlotMachineEvent";
import SlotMachine from "../../../slots/model/SlotMachine";
import { SlotMachineState } from "../../../slots/model/SlotMachineState";
import { UIPanelEvent } from "../../control/event/UIPanelEvent";
import { UIStateEvent } from "../../model/event/UIStateEvent";
import PopupState, { PopupData, PopupType } from "../../model/PopupState";
import UIState from "../../model/UIState";
import { saveToSessionStorage } from "../../../../jewels-of-wizard/model/SessionStorageUtils";

export default class PlayerProtectionGameTimer extends Container {

    private tfTimer: Text;
    private elapsedSeconds: number = 0;
    private lastUpdateTime: number = 0;
    private boundUpdate: (delta: number) => void;
    private limitTotalSeconds: number = 0;
    private repeatTotalSeconds: number = 0;
    private slotMachineInstance: SlotMachine;
    private uiStateInstance: UIState;
    private popupStateInstance: PopupState;
    private pendingWarning: boolean = false;
    private isPaused: boolean = false;
    private boundVisibilityChange: () => void;
    constructor(
        le: LayoutElement,
        startFrom: { hours: number, minutes: number, seconds: number } = { hours: 0, minutes: 0, seconds: 0 },
        private timeLimit: { hours: number, minutes: number, seconds: number },
        private repeatInterval: { hours: number, minutes: number, seconds: number }
    ) {
        super();

        LayoutBuilder.create(le, this, (le: LayoutElement) => {
            return this.customClassElementCreate(le);
        });

        this.slotMachineInstance = container.resolve(SlotMachine);
        this.uiStateInstance = container.resolve(UIState);
        this.popupStateInstance = container.resolve(PopupState);

        this.slotMachineInstance.on(SlotMachineEvent.STATE_CHANGED, this.onSlotMachineStateChanged, this);
        this.uiStateInstance.on(UIStateEvent.ACTIVE_PANEL_CHANGED, this.onUiActivePanelChanged, this);
        this.popupStateInstance.on(UIStateEvent.ACTIVE_POPUP_CHANGED, this.onActivePopupChanged, this);

        this.timeLimit = timeLimit;
        this.repeatInterval = repeatInterval;
        this.elapsedSeconds = this.timeObjToSeconds(startFrom);

        this.repeatTotalSeconds = this.timeObjToSeconds(this.repeatInterval);
        this.limitTotalSeconds = this.timeObjToSeconds(this.timeLimit);

        if(this.elapsedSeconds >= this.limitTotalSeconds){
            // Adjust the limitTotalSeconds to the next applicable limit
            while(this.elapsedSeconds >= this.limitTotalSeconds){
                this.limitTotalSeconds += this.repeatTotalSeconds;
            }
        }

        this.boundUpdate = this.updateTimer.bind(this);
        this.boundVisibilityChange = this.onVisibilityChange.bind(this);

        this.on('added', this.onAdded, this);
        this.on('removed', this.onRemoved, this);
    }

    // PRIVATE API
    private onSlotMachineStateChanged(currentState: SlotMachineState): void {
        if (currentState === SlotMachineState.IDLE && this.pendingWarning) {
            this.showWarningPopup();
            this.checkForMissedLimits();
        }
    }

    private checkForMissedLimits(): void {
        while (this.elapsedSeconds >= this.limitTotalSeconds) {
            this.limitTotalSeconds += this.repeatTotalSeconds;
            this.pendingWarning = true;
        }
    }

    private onAdded(): void {
        document.addEventListener('visibilitychange', this.boundVisibilityChange);
        this.initTimer();
    }

    private onRemoved(): void {
        document.removeEventListener('visibilitychange', this.boundVisibilityChange);
        this.destroyTimer();
    }

    private initTimer(): void {
        this.lastUpdateTime = Date.now();
        Ticker.shared.add(this.boundUpdate);
    }

    private destroyTimer(): void {
        Ticker.shared.remove(this.boundUpdate);
    }

    private updateTimer(): void {
        if (this.isPaused) return;
        const currentTime = Date.now();
        const deltaTime = (currentTime - this.lastUpdateTime) / 1000;

        if (deltaTime >= 1) {
            const secondsToAdd = Math.floor(deltaTime);
            this.elapsedSeconds += secondsToAdd;
            this.lastUpdateTime += secondsToAdd * 1000;
            this.updateTimerText();
        }
    }

    private updateTimerText(): void {
        this.checkAndUpdateTimeLimits();
        const { hours, minutes, seconds } = this.secondsToTimeObj(this.elapsedSeconds);
        this.tfTimer.text = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
        const params: URLSearchParams = new URLSearchParams(window.location.search);
        const sessionId: string = params.get('session') ?? '';
        saveToSessionStorage(`session_timer:${sessionId}`, {
            elapsedSeconds: this.elapsedSeconds,
            lastUpdatedAt: Date.now()
        });
    }

    private checkAndUpdateTimeLimits(): void {
        if (this.elapsedSeconds >= this.limitTotalSeconds) {
            this.limitTotalSeconds += this.repeatTotalSeconds;
            this.updateTimeLimitObject();

            if (this.slotMachineInstance.currentState === SlotMachineState.IDLE) {
                this.showWarningPopup();
            } else {
                this.pendingWarning = true;
                new ControlEvent(UIEvent.AUTO_SPIN).dispatch();
            }
        }
    }

    private updateTimeLimitObject(): void {
        const { hours, minutes, seconds } = this.secondsToTimeObj(this.limitTotalSeconds);
        this.timeLimit.hours = hours;
        this.timeLimit.minutes = minutes;
        this.timeLimit.seconds = seconds;
    }

    private showWarningPopup(): void {
        if (this.uiStateInstance.activePanel || this.popupStateInstance.activePopup) {
            if (this.popupStateInstance.activePopup?.type !== PopupType.PLAYER_PROTECTION_WARNING) {
                this.pendingWarning = true;
            }
            return;
        }

        const data: PopupData = {
            type: PopupType.PLAYER_PROTECTION_WARNING,
            hideOnClick: false,
            duration: -1,
            callbacks: {
                onPopupShown: this.pauseTimer.bind(this),
                onPopupHidden: this.resumeTimer.bind(this)
            }
        };
        new ControlEvent(UIPanelEvent.SHOW_POPUP, data).dispatch();
        this.pendingWarning = false;
    }

    private timeObjToSeconds(t: { hours: number, minutes: number, seconds: number }): number {
        return (t.hours * 3600) + (t.minutes * 60) + t.seconds;
    }

    private secondsToTimeObj(total: number): { hours: number, minutes: number, seconds: number } {
        const hours = Math.floor(total / 3600);
        const minutes = Math.floor((total % 3600) / 60);
        const seconds = total % 60;
        return { hours, minutes, seconds };
    }

    private pauseTimer(): void {
        if (!this.isPaused) {
            this.isPaused = true;
            Ticker.shared.remove(this.boundUpdate);
        }
    }

    private resumeTimer(): void {
        if (this.isPaused && this.popupStateInstance.activePopup?.type !== PopupType.PLAYER_PROTECTION_WARNING) {
            this.isPaused = false;
            this.lastUpdateTime = Date.now();
            Ticker.shared.add(this.boundUpdate);
        }
    }

    private onVisibilityChange(): void {
        if (document.visibilityState === 'hidden') {
            this.pauseTimer();
        } else {
            this.resumeTimer();
        }
    }

    private onActivePopupChanged(): void {
        // Delay to ensure if there's any recurring popup logic
        Tweener.addCaller(this, {
            time: 0.1,
            count: 1,
            onComplete: () => {
                if (!this.popupStateInstance.activePopup && this.pendingWarning && this.slotMachineInstance.currentState === SlotMachineState.IDLE) {
                    this.showWarningPopup();
                }
            }
        })
    }

    private onUiActivePanelChanged(): void {
        // Delay to ensure if there's any recurring panel logic
        // Espesially in the case of closing a panel then triggering another popup (e.g. history panel getting opened from settings panel)
        Tweener.addCaller(this, {
            time: 0.1,
            count: 1,
            onComplete: () => {
                if (!this.uiStateInstance.activePanel && this.pendingWarning && this.slotMachineInstance.currentState === SlotMachineState.IDLE) {
                    this.showWarningPopup();
                }
            }
        });
    }

    private customClassElementCreate(le: LayoutElement): unknown {
        let instance: unknown = null;
        switch (le.customClass) {

        }
        return instance;
    }

    // PUBLIC API
    public get currentTime(): { hours: number, minutes: number, seconds: number } {
        return this.secondsToTimeObj(this.elapsedSeconds);
    }
}